import Button from 'common/components/Button';
import { scrollToElement } from 'common/utils/scroll';
import SliderField from 'launch/components/SliderField';
import React from 'react';
import { FormattedHTMLMessage, intlShape } from 'react-intl';
import ColorMap, { ColorMapColor, ColorMapValues } from './ColorMap';
import ColorSliderAppResult from './ColorSliderAppResult';

interface State {
    loading: boolean;
    resultColor: ColorResult | null;
    sliderValues: ColorMapValues;
}

export interface PantoneColor {
    name: string;
    colorId: string;
}

export interface ColorResult {
    color: ColorMapColor;
    hex: string;
    pantone: PantoneColor | null;
    designer: string;
}

export default class ColorSliderApp extends React.Component<{}, State> {
    readonly colors: ColorMap;
    private loadingSpinnerRef: React.RefObject<HTMLImageElement>;

    constructor(props: {}) {
        super(props);
        this.state = {
            loading: false,
            resultColor: null,
            sliderValues: {
                feminineMasculine: 0,
                playfulSerious: 0,
                luxuriousAffordable: 0,
                modernClassic: 0,
                youthfulMature: 0,
                loudSubdued: 0,
            },
        };
        this.colors = new ColorMap();
        this.loadingSpinnerRef = React.createRef();
    }

    static contextTypes = {
        intl: intlShape.isRequired,
    };

    saveSliderState(key: string, value: number) {
        this.setState(state => {
            const sliderValues = { ...state.sliderValues };
            sliderValues[key] = value;
            return {
                resultColor: null,
                sliderValues: sliderValues,
            };
        });
    }

    sliderData() {
        const { formatMessage: t } = this.context.intl;
        return [
            {
                minLabel: t({ id: 'psych_of_colors.slider.feminine' }),
                maxLabel: t({ id: 'psych_of_colors.slider.masculine' }),
                key: 'feminineMasculine',
            },
            {
                minLabel: t({ id: 'psych_of_colors.slider.playful' }),
                maxLabel: t({ id: 'psych_of_colors.slider.serious' }),
                key: 'playfulSerious',
            },
            {
                minLabel: t({ id: 'psych_of_colors.slider.luxurious' }),
                maxLabel: t({ id: 'psych_of_colors.slider.affordable' }),
                key: 'luxuriousAffordable',
            },
            {
                minLabel: t({ id: 'psych_of_colors.slider.classic' }),
                maxLabel: t({ id: 'psych_of_colors.slider.modern' }),
                key: 'modernClassic',
            },
            {
                minLabel: t({ id: 'psych_of_colors.slider.youthful' }),
                maxLabel: t({ id: 'psych_of_colors.slider.mature' }),
                key: 'youthfulMature',
            },
            {
                minLabel: t({ id: 'psych_of_colors.slider.subdued' }),
                maxLabel: t({ id: 'psych_of_colors.slider.loud' }),
                key: 'loudSubdued',
            },
        ];
    }

    componentDidUpdate(prevProps: {}, prevState: State) {
        if (prevState.loading === false && this.state.loading === true) {
            if (this.loadingSpinnerRef.current) {
                scrollToElement(this.loadingSpinnerRef.current, 50);
            }
        }
    }

    render() {
        return (
            <div>
                <div className="section">
                    <div className="row">
                        <div className="column">
                            <div className="long-form-free-content">
                                <div className="heading heading--h2 heading--h2-capped heading--with-divider">
                                    <FormattedHTMLMessage id="psych_of_colors.title" />
                                </div>
                                <p className="paragraph paragraph--offset paragraph--narrow">
                                    <FormattedHTMLMessage id="psych_of_colors.intro" />
                                </p>
                                {this.renderSliders()}
                                <div className="centered-text">{this.renderCta()}</div>
                            </div>
                        </div>
                    </div>
                </div>
                {this.prefetchImages()}
                {this.hasColorResultForDisplay() ? (
                    <ColorSliderAppResult resultColor={this.state.resultColor!} />
                ) : null}
            </div>
        );
    }

    hasColorResult() {
        return !!this.state.resultColor;
    }

    hasColorResultForDisplay() {
        return this.hasColorResult() && !this.state.loading;
    }

    calculateColor() {
        const color = this.colors.getColor(this.state.sliderValues);
        const hex = this.colors.getColorHexValue(color);
        const pantoneColor = this.getPantoneColor(color);
        const designer = this.getDesigner(color);
        const result: ColorResult = {
            color: color,
            hex: hex,
            pantone: pantoneColor,
            designer: designer,
        };
        return result;
    }

    getPantoneColor(color: ColorMapColor) {
        const invalidPantoneColors = [
            ColorMapColor.Black,
            ColorMapColor.Gray,
            ColorMapColor.White,
            ColorMapColor.Brown,
        ];
        if (invalidPantoneColors.includes(color)) {
            return null;
        }

        type pantoneColorMap = { [key: string]: PantoneColor };
        const pantoneColors: pantoneColorMap = {
            [ColorMapColor.Blue]: {
                name: 'Hawaiian Ocean',
                colorId: '17-4540 TCX',
            },
            [ColorMapColor.Green]: {
                name: 'Lime Green',
                colorId: '14-0452 TCX',
            },
            [ColorMapColor.Purple]: {
                name: 'Fuchsia Purple',
                colorId: '18-2436 TCX',
            },
            [ColorMapColor.Pink]: {
                name: 'Fuchsia Purple',
                colorId: '18-2436 TCX',
            },
            [ColorMapColor.Red]: {
                name: 'Cherry Tomato',
                colorId: '17-1563 TCX',
            },
            [ColorMapColor.Orange]: {
                name: 'Flame Orange',
                colorId: '15-1157 TCX',
            },
            [ColorMapColor.Yellow]: {
                name: 'Blazing Yellow',
                colorId: '12-0643 TCX',
            },
        };

        return pantoneColors[color];
    }

    getDesigner(color: ColorMapColor) {
        type designerMap = { [key in ColorMapColor]: string };
        const designers: designerMap = {
            [ColorMapColor.Pink]: 'green in blue',
            [ColorMapColor.Green]: 'Martis Lupus',
            [ColorMapColor.Blue]: 'St. Pelmeni',
            [ColorMapColor.Red]: 'olimpio',
            [ColorMapColor.Orange]: 'Mky',
            [ColorMapColor.Yellow]: 'Push™',
            [ColorMapColor.Purple]: 'Iconic Graphics',
            [ColorMapColor.Brown]: 'Project 4',
            [ColorMapColor.Black]: 'goopanic',
            [ColorMapColor.White]: 'shevatm',
            [ColorMapColor.Gray]: 'Yokaona',
        };

        return designers[color];
    }

    getColorHexValueForSlider() {
        if (this.hasColorResultForDisplay()) {
            if (this.state.resultColor!.color === ColorMapColor.White) {
                return null;
            }
            return this.state.resultColor!.hex;
        }
        return null;
    }

    renderCta() {
        if (this.state.loading) {
            return this.renderLoadingSpinner();
        }
        return (
            <Button
                size="jumbo"
                variation="primary"
                responsive={true}
                color="grey800"
                onClick={() => this.handleCtaClick()}
            >
                <FormattedHTMLMessage id="psych_of_colors.cta" />
            </Button>
        );
    }

    renderLoadingSpinner() {
        return (
            <img
                ref={this.loadingSpinnerRef}
                className="psych-of-color-spinner psych-of-color-spinner--animate"
                src={require('images/psych-of-color/poc-spinner.png')}
            />
        );
    }

    renderSliders() {
        return this.sliderData().map(item => {
            return (
                <SliderField
                    key={item.key}
                    minLabel={item.minLabel}
                    maxLabel={item.maxLabel}
                    onChange={this.saveSliderState.bind(this, item.key)}
                    steps={5}
                    initialValue={0}
                    color={this.getColorHexValueForSlider()}
                />
            );
        });
    }

    handleCtaClick() {
        const loading = true;
        const resultColor = this.calculateColor();

        this.setState(state => ({
            loading: loading,
            resultColor: resultColor,
        }));

        setTimeout(() => {
            this.setState(state => ({
                loading: false,
            }));
        }, 2000);
    }

    prefetchImages() {
        return (
            <div>
                {this.renderCurrentColorResultImage()}
                {this.renderHiddenImage(require('images/psych-of-color/poc-spinner.png'))}
            </div>
        );
    }

    renderHiddenImage(url: string) {
        return <img src={url} style={{ display: 'none' }} />;
    }

    renderCurrentColorResultImage() {
        if (this.hasColorResult()) {
            const color = this.state.resultColor!.color;
            const url = require(`images/psych-of-color/${color}.png`);
            return this.renderHiddenImage(url);
        }
        return null;
    }
}
